import {
  Suspense,
  lazy,
  useState,
  useEffect,
  memo,
  useCallback,
  useMemo,
} from 'react';
import { Button, Center, Portal, Stack, Text } from '@mantine/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useGeoMarkupStore } from '@/store';
import { VIEW_MODE } from '@/types';
import { getArtifact, getAllArtifacts } from '@/artifacts';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { usePreventZoom } from '@/hooks/usePreventZoom';
import {
  useTaskNavigation,
  useBackgroundLocation,
  resolveBackgroundArtifact,
} from '@/hooks';
import { useMapViewMode } from '@/hooks/useMapViewMode';
import { useSyncOverlayWithUrl } from '@/hooks/useSyncOverlayWithUrl';
import { MAP_DEFERRED_LOAD_DELAY } from '@/constants/ui';
import { MapErrorBoundary } from '@/components/map/MapErrorBoundary';
import { MapFallback } from '@/components/ui/LazyComponentFallback';
import { SidePanel } from '@/components/ui/SidePanel';
import { ArtifactBar } from '@/components/ui/SidePanel/ArtifactBar';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { UnifiedDetailOverlay } from '@/components/overlays/UnifiedDetailOverlay';
import {
  TopBar,
  TopBarActions,
  TopBarDivider,
} from '@/components/layout/AppShell/TopBar';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { MapBackgroundRoutes } from './MapBackgroundRoutes';
import styles from './MapView.module.css';

const MapContainer = lazy(() =>
  import('@/components/map/MapContainer').then((module) => ({
    default: module.MapContainer,
  }))
);

/**
 * Shared map layout for the unified `/map` route.
 *
 * Renders a **background** (tasks/notes page) and an **overlay** (task/note
 * detail/form) as two independent dimensions of URL state. Uses the React
 * Router modal pattern via `location.state.backgroundLocation`:
 *
 * - `useBackgroundLocation()` returns the location that should be used for
 *   background routing (either the current location or the stored one).
 * - Background routes are matched via `MapBackgroundRoutes` with that
 *   location, so the panel stays mounted under an overlay.
 * - Overlay panels (TaskDetailsPanel / NoteDetailPanel / TaskFormWizard /
 *   NoteFormPanel) are rendered unconditionally; their visibility is
 *   driven by the artifact slices (`state.tasks.*`, `state.notes.*`),
 *   kept in sync with the URL via `useSyncOverlayWithUrl`.
 *
 * Hosts the single SidePanel that switches content based on the active
 * background artifact (tasks|notes).
 */
export const MapView = memo(function MapView() {
  const { t } = useTranslation();
  const { isDesktop } = useDeviceLayout();
  const backgroundLocation = useBackgroundLocation();

  // Sync overlay URL (task/note detail/edit/new) with store flags that
  // drive panel visibility.
  useSyncOverlayWithUrl();

  const { view: activeView } = useMapViewMode();

  const isSidePanelOpen = useGeoMarkupStore((s) => s.ui.sidePanel.isOpen);
  const toggleSidePanel = useGeoMarkupStore((s) => s.ui.toggleSidePanel);

  // Navigation conflict dialog (task-to-task navigation while form is open)
  const { showNavigationDialog, executePendingNavigation, cancelNavigation } =
    useTaskNavigation();

  // The unsaved-changes guard dialog (opening a form / switching workspace
  // while another form is dirty) is rendered once globally in AppShell so it
  // is also available when the switch is triggered from the top bar.

  const activeArtifact = resolveBackgroundArtifact(backgroundLocation.pathname);
  const isMapMode = activeView === VIEW_MODE.MAP;
  const PanelContent =
    getArtifact(activeArtifact)?.PanelContent ??
    getAllArtifacts()[0]?.PanelContent;

  const fullscreenTopBar = useMemo(() => {
    if (!isMapMode) return undefined;

    return (
      <TopBar
        hideLogo
        actions={
          <>
            <ViewToggle />
            <TopBarDivider />
            <TopBarActions variant="compact" />
          </>
        }
      />
    );
  }, [isMapMode]);

  const [shouldLoadMap, setShouldLoadMap] = useState(false);

  usePreventZoom();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadMap(true);
    }, MAP_DEFERRED_LOAD_DELAY);

    return () => clearTimeout(timer);
  }, []);

  // Auto-open side panel on desktop mount, close on unmount
  useEffect(() => {
    if (isDesktop) {
      toggleSidePanel(true);
    }
    return () => {
      toggleSidePanel(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseSidePanel = useCallback(
    () => toggleSidePanel(false),
    [toggleSidePanel]
  );

  return (
    <div className={styles.container}>
      {isMapMode && (
        <div
          className={clsx(
            styles.mapSection,
            isDesktop && isSidePanelOpen && styles.mapSectionWithSidePanel
          )}
        >
          <MapErrorBoundary>
            {shouldLoadMap ? (
              <Suspense fallback={<MapFallback />}>
                <MapContainer />
              </Suspense>
            ) : (
              <Center className={styles.fullHeightCenter}>
                <Stack align="center" gap="md">
                  <Text size="lg" c="dimmed">
                    {t('app.loadingMap')}
                  </Text>
                  <Button
                    variant="light"
                    onClick={() => setShouldLoadMap(true)}
                  >
                    {t('app.loadMapNow')}
                  </Button>
                </Stack>
              </Center>
            )}
          </MapErrorBoundary>
        </div>
      )}

      {isMapMode && (
        <Portal>
          <SidePanel
            isOpen={isSidePanelOpen}
            onClose={handleCloseSidePanel}
            footer={<ArtifactBar activeArtifact={activeArtifact} />}
          >
            {PanelContent && (
              <Suspense fallback={null}>
                <PanelContent onClose={handleCloseSidePanel} />
              </Suspense>
            )}
          </SidePanel>
        </Portal>
      )}

      {/*
        Background routes (tasks/notes pages). Matched against the
        background location so the page stays mounted under any overlay.
      */}
      <MapBackgroundRoutes location={backgroundLocation} />

      {/* Unified detail overlay — single SlidePanel shell shared by every
          artifact's `DetailContent`. Swapping artifacts (e.g. clicking a
          linked task from a note detail) happens inside the shell without
          animating close+open. Visibility is driven by the OR of every
          artifact's `detailsIsOpen` flag (kept in sync with the URL via
          `useSyncOverlayWithUrl`). */}
      <UnifiedDetailOverlay fullscreenTopBar={fullscreenTopBar} />

      {/* Form panels — one per artifact, visibility driven by each
          slice's `formIsOpen` flag. Each artifact declares its own
          FormPanel in the registry, so adding a new artifact does not
          require editing this file. */}
      {getAllArtifacts().map((artifact) => {
        const FormPanel = artifact.FormPanel;
        return <FormPanel key={artifact.id} />;
      })}

      {/* Navigation conflict dialog (task-to-task while form is open) */}
      <ConfirmationModal
        opened={showNavigationDialog}
        onClose={cancelNavigation}
        onConfirm={executePendingNavigation}
        title={t('dialogs.navigationConflict.title')}
        message={t('dialogs.navigationConflict.message')}
        variant="danger"
        confirmLabel={t('dialogs.navigationConflict.confirm')}
        cancelLabel={t('dialogs.navigationConflict.cancel')}
      />
    </div>
  );
});
